import { Location } from "@angular/common";
import { Inject, Injectable } from "@angular/core";

import { INFRA_SETTINGS, LOCALE } from "@hermes/app-core";
import { InfraSettings } from "@hermes/env-infra";
import { Locale } from "@hermes/locale";
import {
    getCityBreadcrumbList,
    getCountryBreadcrumbList,
    MicroDataUrl,
} from "@hermes/map-utils";
import {
    CurrentShopLocation,
    SHOP_LOCATIONS_CITY_MODE,
    SHOP_LOCATIONS_COUNTRY_MODE,
} from "@hermes/states/shop-locations";
import { HeadService } from "@hermes/utils-generic/services/head";

@Injectable()
export class ShopLocatorPageHeadService {
    constructor(
        @Inject(INFRA_SETTINGS) private infraSettings: InfraSettings,
        @Inject(LOCALE) private locale: Locale,
        private headService: HeadService,
        private location: Location,
    ) {}

    public initHeadTag(currentLocation: CurrentShopLocation): void {
        this.headService.cleanExistingMetas();
        this.headService.addCanonicalUrl(
            this.getCurrentUrl().replace(/\?.*/, ""),
        );

        this.manageMetaTitle(currentLocation);
        this.manageMetaDescription(currentLocation);
        this.manageMicroData(currentLocation);
    }

    public removeHeadTags(): void {
        this.headService.cleanDescriptionMetaInformations();
        this.headService.removeScriptById("microdataUrl");
        this.headService.cleanCanonicalUrl();
    }

    private getCurrentUrl(): string {
        const currentUrl = `${this.infraSettings.frontend}${
            this.locale.urlPrefix
        }${this.location.path()}`;
        return decodeURI(currentUrl);
    }

    private manageMicroData(currentLocation: CurrentShopLocation): void {
        const findStoreUrl = `${this.infraSettings.frontend}${this.locale.urlPrefix}/find-store/`;

        let microDataUrl!: MicroDataUrl;
        if (currentLocation.pageMode === SHOP_LOCATIONS_COUNTRY_MODE) {
            microDataUrl = getCountryBreadcrumbList(findStoreUrl, {
                slugifiedCountry:
                    currentLocation.currentCountry.slugifiedCountry,
                translatedCountry:
                    currentLocation.currentCountry.translatedCountry,
            });
        } else if (currentLocation.pageMode === SHOP_LOCATIONS_CITY_MODE) {
            microDataUrl = getCityBreadcrumbList(findStoreUrl, {
                translatedCity: currentLocation.currentCity.translatedCity,
                slugifiedCity: currentLocation.currentCity.slugifiedCity,
                slugifiedCountry:
                    currentLocation.currentCountry.slugifiedCountry,
                translatedCountry:
                    currentLocation.currentCountry.translatedCountry,
            });
        }

        if (microDataUrl) {
            this.headService.addScript(
                JSON.stringify(microDataUrl),
                "application/ld+json",
                "microdataUrl",
            );
        }
    }

    private manageMetaDescription(currentLocation: CurrentShopLocation): void {
        let description: string;
        switch (currentLocation.pageMode) {
            case SHOP_LOCATIONS_COUNTRY_MODE: {
                description = $localize`:@@hermes.store_locator.page_country_meta_description:Find your Hermès store in ${currentLocation.currentCountry.translatedCountry}:INTERPOLATION: : ready-to-wear, bags, jewelry, and many other Hermès products to discover in your nearest store !`;
                break;
            }
            case SHOP_LOCATIONS_CITY_MODE: {
                description = $localize`:@@hermes.store_locator.page_city_meta_description:Find our Hermès stores in ${currentLocation.currentCity.translatedCity}:INTERPOLATION:, ${currentLocation.currentCountry.translatedCountry}:INTERPOLATION_1:. Hours, addresses and practical information for our Hermès stores in ${currentLocation.currentCity.translatedCity}:INTERPOLATION_2:.`;
                break;
            }
            default: {
                description = $localize`:@@hermes-global-translations.store-locator.meta-description:Find a Hermès store near you. More than 300 Hermès stores around the world.`;
                break;
            }
        }
        this.headService.addDescriptionMetaInformations(description);
    }

    private manageMetaTitle(currentLocation: CurrentShopLocation): void {
        let title: string;
        switch (currentLocation.pageMode) {
            case SHOP_LOCATIONS_COUNTRY_MODE: {
                title = $localize`:@@hermes.store_locator.page_country_title_tag:Hermes Stores ${currentLocation.currentCountry.translatedCountry}:INTERPOLATION:`;
                break;
            }
            case SHOP_LOCATIONS_CITY_MODE: {
                title = $localize`:@@hermes.store_locator.page_country_title_tag:Hermes Stores ${currentLocation.currentCity.translatedCity}:INTERPOLATION:`;
                break;
            }
            default: {
                title = $localize`:@@hermes.store_locator.page_title_tag:Find a store`;
                break;
            }
        }
        this.headService.setTitle(title);
    }
}
